import type { Participant, Room, Bus, GroupResponsibleState } from '@/scopes/account/group-responsible/domain/types';
import type { IntlShape } from 'react-intl';

function valOrDash(value: string | number | undefined | null): string {
  return value != null && value !== '' ? String(value) : '-';
}

function translateHeaders(intl: IntlShape, ids: string[]): string[] {
  return ids.map((id) => intl.formatMessage({ id }));
}

function getParticipantHeaders(intl: IntlShape): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_name_label',
    'groups_responsible_table_status_label',
    'groups_responsible_room',
    'groups_responsible_table_amount_due_label',
    'email_address',
    'groups_responsible_booking_number',
    'groups_responsible_phone',
    'groups_responsible_mobile_phone',
    'groups_responsible_accommodation',
    'groups_responsible_room_type',
    'groups_responsible_ski_pass',
    'groups_responsible_ski_material',
    'groups_responsible_totally_sweater',
  ]);
}

function getRoomHeaders(intl: IntlShape): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_room_name_label',
    'groups_responsible_room_type',
    'groups_responsible_table_room_occupancy_label',
    'groups_responsible_accommodation',
    'groups_responsible_hosts',
  ]);
}

function getBusHeaders(intl: IntlShape): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_name_label',
    'groups_responsible_table_bus_seat_status_label',
    'groups_responsible_table_bus_stop_label',
    'groups_responsible_table_bus_number_label',
    'email_address',
    'groups_responsible_phone',
    'groups_responsible_mobile_phone',
    'groups_responsible_accommodation',
    'groups_responsible_room_type',
    'groups_responsible_ski_pass',
    'groups_responsible_ski_material',
    'groups_responsible_totally_sweater',
  ]);
}

function getParticipantRoom(state: GroupResponsibleState, participantId: number) {
  return Object.values(state.rooms).find((room) => room.participantsId.includes(participantId));
}

function formatParticipantRow(participant: Participant, roomData: Room | undefined, intl: IntlShape): string[] {
  const status = participant.status?.text ? intl.formatMessage({ id: participant.status.text }) : '-';
  const room = participant.room ? intl.formatMessage({ id: participant.room }) : '-';

  return [
    valOrDash(participant.name),
    status,
    room,
    valOrDash(participant.amountDue),
    valOrDash(participant.email),
    valOrDash(participant.bookingNumber),
    valOrDash(participant.telephoneNumber),
    valOrDash(participant.mobileNumber),
    valOrDash(roomData?.accommodationName),
    valOrDash(roomData?.type),
    valOrDash(participant.skiPass),
    participant.hasSkiMaterial
      ? intl.formatMessage({ id: 'groups_responsible_yes' })
      : intl.formatMessage({ id: 'groups_responsible_no' }),
    participant.hasTotallySweater
      ? intl.formatMessage({ id: 'groups_responsible_yes' })
      : intl.formatMessage({ id: 'groups_responsible_no' }),
  ];
}

function formatBusParticipantRow(
  participant: Participant,
  roomData: Room | undefined,
  busData: Bus[] | undefined,
  intl: IntlShape
): string[] {
  const seatStatusMessageId = getSeatStatusMessageId(busData, participant);
  const seatStatus = intl.formatMessage({ id: seatStatusMessageId });
  const [outboundBus, inboundBus] = [
    busData?.find((bus) => bus.direction === 'Outbound'),
    busData?.find((bus) => bus.direction === 'Inbound'),
  ];

  const departureLocation = outboundBus?.departureLocation;
  const arrivalLocation = inboundBus?.arrivalLocation;

  let location = '-';
  if (departureLocation || arrivalLocation) {
    location = [departureLocation, arrivalLocation].filter(Boolean).join(' - ');
  } else if (participant.assigned) {
    location = intl.formatMessage({ id: 'groups_responsible_table_self_drive_label' });
  }
  return [
    valOrDash(participant.name),
    seatStatus,
    location,
    valOrDash(outboundBus?.busNumber),
    valOrDash(participant.email),
    valOrDash(participant.telephoneNumber),
    valOrDash(participant.mobileNumber),
    valOrDash(roomData?.accommodationName),
    valOrDash(roomData?.type),
    valOrDash(participant.skiPass),
    participant.hasSkiMaterial
      ? intl.formatMessage({ id: 'groups_responsible_yes' })
      : intl.formatMessage({ id: 'groups_responsible_no' }),
    participant.hasTotallySweater
      ? intl.formatMessage({ id: 'groups_responsible_yes' })
      : intl.formatMessage({ id: 'groups_responsible_no' }),
  ];
}

function getSeatStatusMessageId(busData: Bus[] | undefined, participant: Participant): string {
  if (busData?.length) return 'groups_responsible_status_booked';
  if (participant.assigned) return 'groups_responsible_status_not_applicable';
  return 'groups_responsible_status_not_booked';
}

export function getParticipantTableData(state: GroupResponsibleState, intl: IntlShape): string[][] {
  const tableData = [getParticipantHeaders(intl)];

  if (state.participants) {
    for (const participant of Object.values(state.participants)) {
      const participantId = Number(participant?.id);
      const roomData = getParticipantRoom(state, participantId);
      tableData.push(formatParticipantRow(participant, roomData, intl));
    }
  }

  return tableData;
}

export function getRoomsTableData(state: GroupResponsibleState, intl: IntlShape): string[][] {
  const tableData = [getRoomHeaders(intl)];

  if (state.rooms) {
    for (const room of Object.values(state.rooms)) {
      const participants = room.participants?.length ? room.participants.join('\n') : '-';

      const type = room.type ? intl.formatMessage({ id: room.type }) : '-';

      tableData.push([
        valOrDash(room.name),
        type,
        valOrDash(room.occupancyText),
        valOrDash(room.accommodationName),
        participants,
      ]);
    }
  }

  return tableData;
}

export function getBusTableData(state: GroupResponsibleState, intl: IntlShape): string[][] {
  const tableData = [getBusHeaders(intl)];

  if (state.participants) {
    for (const participant of Object.values(state.participants)) {
      const participantId = Number(participant?.id);

      const busData = Object.values(state.buses).filter((bus) => bus.participantsId.includes(participantId));
      const roomData = getParticipantRoom(state, participantId);

      tableData.push(formatBusParticipantRow(participant, roomData, busData, intl));
    }
  }

  return tableData;
}

export function getTableData(state: GroupResponsibleState, intl: IntlShape): [string[][], string] {
  switch (state.activeTab) {
    case 'participants':
      return [
        getParticipantTableData(state, intl),
        intl.formatMessage({ id: 'groups_responsible_table_participants' }),
      ];
    case 'rooms':
      return [getRoomsTableData(state, intl), intl.formatMessage({ id: 'groups_responsible_table_rooms' })];
    case 'bus':
      return [getBusTableData(state, intl), intl.formatMessage({ id: 'groups_responsible_table_bus_schedule' })];
    default:
      return [[], ''];
  }
}
